/**
 * Procedural vessel geometry.
 *
 * Self-contained by design: the rig has its own accumulator inside rigBuilder.ts and that
 * file is deliberately left alone, so this module carries a small independent one. It is
 * simpler than the rig's — no bevelling, no physical UV projection — because hulls are
 * smooth-sided and read at range, where edge weathering does nothing.
 *
 * Why procedural rather than sourced CC0 kits: the asset plan pre-clears both tracks, and
 * the quality bar makes the choice for us. Low-poly pack hulls are explicitly "starting
 * geometry only" and never acceptable for anything visible through the 8× scope — which
 * is every vessel in this game. Since each hull would need a full detailing pass anyway,
 * generating them gives the same result with exact control over silhouette, scale and
 * waterline, and with no licence surface at all.
 */

import { Mesh } from '@babylonjs/core/Meshes/mesh';
import { VertexData } from '@babylonjs/core/Meshes/mesh.vertexData';
import { Matrix, Vector3, Quaternion } from '@babylonjs/core/Maths/math.vector';
import { CreateBoxVertexData } from '@babylonjs/core/Meshes/Builders/boxBuilder';
import { CreateCylinderVertexData } from '@babylonjs/core/Meshes/Builders/cylinderBuilder';
import { MultiMaterial } from '@babylonjs/core/Materials/multiMaterial';
import { SubMesh } from '@babylonjs/core/Meshes/subMesh';
import type { Scene } from '@babylonjs/core/scene';
import type { Material } from '@babylonjs/core/Materials/material';

export type Rgb = readonly [number, number, number];

/**
 * A vessel stays one mesh, but the mesh may contain a few physically different surfaces.
 * Babylon draws one submesh per surface while still sharing one transform and one vertex
 * buffer. That is the useful middle ground between "everything looks like steel" and a
 * prop-per-draw model that would destroy the fleet's draw-call discipline.
 */
export type VesselSurface =
  | 'paint'
  | 'deck'
  | 'glass'
  | 'metal'
  | 'rubber'
  | 'organic'
  | 'eye'
  | 'pupil'
  | 'navRed'
  | 'navGreen'
  | 'lamp';

export type VesselMaterials = Record<VesselSurface, Material>;

export interface VesselDeformation {
  /** Zero leaves the vertex rigid; positive values select a deforming limb. */
  group: number;
  /** Position along the limb, either one value for the primitive or one per vertex. */
  t: number | ArrayLike<number>;
}

const SURFACE_ORDER: readonly VesselSurface[] = [
  'paint',
  'deck',
  'glass',
  'metal',
  'rubber',
  'organic',
  'eye',
  'pupil',
  'navRed',
  'navGreen',
  'lamp',
];

/** Accumulates primitives into one vertex buffer and a small set of material submeshes. */
export class HullAccumulator {
  private positions: number[] = [];
  private normals: number[] = [];
  private uvs: number[] = [];
  private deformationUvs: number[] = [];
  private colors: number[] = [];
  private hasDeformation = false;
  private readonly indices = new Map<VesselSurface, number[]>(
    SURFACE_ORDER.map((surface) => [surface, []]),
  );

  add(
    data: VertexData,
    transform: Matrix,
    tint: Rgb,
    surface: VesselSurface = 'paint',
    uvScale = 0.25,
    deformation?: VesselDeformation,
  ): void {
    const base = this.positions.length / 3;
    const pos = data.positions as number[];
    const nrm = data.normals as number[];
    const idx = data.indices as number[];

    const normalMatrix = transform.clone();
    normalMatrix.setTranslationFromFloats(0, 0, 0);

    const p = new Vector3();
    const n = new Vector3();
    for (let i = 0; i < pos.length; i += 3) {
      p.set(pos[i] as number, pos[i + 1] as number, pos[i + 2] as number);
      const tp = Vector3.TransformCoordinates(p, transform);
      this.positions.push(tp.x, tp.y, tp.z);

      n.set(nrm[i] as number, nrm[i + 1] as number, nrm[i + 2] as number);
      const tn = Vector3.TransformNormal(n, normalMatrix).normalize();
      this.normals.push(tn.x, tn.y, tn.z);

      // Triplanar-ish UVs from world position: cheap, and seams never show on a hull
      // seen from more than a few metres away.
      const ax = Math.abs(tn.x), ay = Math.abs(tn.y), az = Math.abs(tn.z);
      if (ay >= ax && ay >= az) this.uvs.push(tp.x * uvScale, tp.z * uvScale);
      else if (ax >= az) this.uvs.push(tp.z * uvScale, tp.y * uvScale);
      else this.uvs.push(tp.x * uvScale, tp.y * uvScale);

      const vertex = i / 3;
      const deformationT = typeof deformation?.t === 'number'
        ? deformation.t
        : (deformation?.t[vertex] ?? 0);
      this.deformationUvs.push(deformation?.group ?? 0, deformationT);
      if ((deformation?.group ?? 0) > 0) this.hasDeformation = true;

      // Streaked weathering: a vertical gradient plus position noise, so rust runs down
      // from the deck edge the way it does on a real hull.
      const hash = Math.sin(tp.x * 12.9898 + tp.y * 4.1414 + tp.z * 78.233) * 43758.5453;
      const noise = hash - Math.floor(hash);
      const grime = 0.82 + noise * 0.18;
      this.colors.push(tint[0] * grime, tint[1] * grime, tint[2] * grime, 1);
    }

    const surfaceIndices = this.indices.get(surface);
    if (!surfaceIndices) throw new Error(`Unknown vessel surface: ${surface}`);
    for (const v of idx) surfaceIndices.push(base + v);
  }

  build(
    scene: Scene,
    name: string,
    materials: VesselMaterials,
    updatable = false,
  ): Mesh {
    const mesh = new Mesh(name, scene);
    const vd = new VertexData();
    vd.positions = this.positions;
    vd.normals = this.normals;
    vd.uvs = this.uvs;
    // UV2 is unused by the vessel materials. The Kraken uses its two floats as compact
    // deformation metadata: limb id and progress along that limb. Keeping the data in a
    // standard vertex channel means clone/makeGeometryUnique preserve it automatically.
    if (this.hasDeformation) vd.uvs2 = this.deformationUvs;
    vd.colors = this.colors;

    const activeSurfaces = SURFACE_ORDER.filter(
      (surface) => (this.indices.get(surface)?.length ?? 0) > 0,
    );
    const combinedIndices: number[] = [];
    const ranges: Array<{ start: number; count: number }> = [];
    for (const surface of activeSurfaces) {
      const start = combinedIndices.length;
      const surfaceIndices = this.indices.get(surface) ?? [];
      // A close-range Kraken can carry hundreds of thousands of indices in one organic
      // surface. Spreading that array turns every index into a function argument and
      // exceeds JavaScript's call-stack/argument limit before the GPU sees the mesh.
      for (const index of surfaceIndices) combinedIndices.push(index);
      ranges.push({ start, count: surfaceIndices.length });
    }
    vd.indices = combinedIndices;
    vd.applyToMesh(mesh, updatable);

    const multi = new MultiMaterial(`${name}_surfaces`, scene);
    multi.subMaterials = activeSurfaces.map((surface) => materials[surface]);
    mesh.material = multi;
    mesh.subMeshes = [];
    for (let i = 0; i < ranges.length; i++) {
      const range = ranges[i] as { start: number; count: number };
      new SubMesh(
        i,
        0,
        this.positions.length / 3,
        range.start,
        range.count,
        mesh,
        undefined,
        false,
      );
    }

    mesh.useVertexColors = true;
    mesh.isPickable = false;
    mesh.receiveShadows = false;
    return mesh;
  }

  get triangleCount(): number {
    let count = 0;
    for (const indices of this.indices.values()) count += indices.length / 3;
    return count;
  }
}

// --- Primitive helpers -----------------------------------------------------------------

const cache = new Map<string, VertexData>();

export function box(): VertexData {
  let v = cache.get('box');
  if (!v) { v = CreateBoxVertexData({ size: 1 }); cache.set('box', v); }
  return v;
}

export function cylinder(tessellation = 12): VertexData {
  const key = `cyl${tessellation}`;
  let v = cache.get(key);
  if (!v) {
    v = CreateCylinderVertexData({ height: 1, diameter: 1, tessellation });
    cache.set(key, v);
  }
  return v;
}

/** A tapered cylinder — bow sections, funnels, masts. */
export function taper(
  topDiameter: number,
  bottomDiameter: number,
  tessellation = 12,
  cap = Mesh.CAP_ALL,
): VertexData {
  const key = `tap${topDiameter}_${bottomDiameter}_${tessellation}_${cap}`;
  let v = cache.get(key);
  if (!v) {
    v = CreateCylinderVertexData({
      height: 1,
      diameterTop: topDiameter,
      diameterBottom: bottomDiameter,
      tessellation,
      cap,
    });
    cache.set(key, v);
  }
  return v;
}

/**
 * A box-like volume with sloped sides and an optionally swept roof.
 *
 * Patrol-boat cabins and naval bridge faces are never stacks of vertical shipping
 * containers. This tiny frustum supplies the rake that makes them read as fast,
 * purpose-built vessels without spending polygons on invisible detail.
 */
export function frustum(
  topWidth = 0.72,
  topDepth = 0.72,
  topZOffset = 0,
): VertexData {
  const key = `frustum_${topWidth}_${topDepth}_${topZOffset}`;
  let v = cache.get(key);
  if (v) return v;

  const bw = 0.5;
  const bd = 0.5;
  const tw = topWidth * 0.5;
  const td = topDepth * 0.5;
  const positions = [
    -bw, -0.5, -bd, bw, -0.5, -bd, bw, -0.5, bd, -bw, -0.5, bd,
    -tw, 0.5, -td + topZOffset, tw, 0.5, -td + topZOffset,
    tw, 0.5, td + topZOffset, -tw, 0.5, td + topZOffset,
  ];
  const indices = [
    0, 2, 1, 0, 3, 2,
    4, 5, 6, 4, 6, 7,
    0, 1, 5, 0, 5, 4,
    1, 2, 6, 1, 6, 5,
    2, 3, 7, 2, 7, 6,
    3, 0, 4, 3, 4, 7,
  ];
  const normals: number[] = [];
  VertexData.ComputeNormals(positions, indices, normals);
  v = new VertexData();
  v.positions = positions;
  v.indices = indices;
  v.normals = normals;
  cache.set(key, v);
  return v;
}

/**
 * Build one continuous tube through a chain of points.
 *
 * The rings share vertices along the length, so normals interpolate across bends instead
 * of exposing every construction section as a bright band. This is primarily for
 * tentacles, cables and hoses that must read as one flexible body.
 */
export function curvedTube(
  points: readonly Vector3[],
  radii: readonly number[],
  tessellation = 12,
  capped = true,
): VertexData {
  if (points.length < 2 || points.length !== radii.length) {
    throw new Error('curvedTube requires matching point and radius chains');
  }

  const positions: number[] = [];
  const indices: number[] = [];
  const reference = new Vector3(0, 0, 1);

  for (let i = 0; i < points.length; i++) {
    const prev = points[Math.max(0, i - 1)] as Vector3;
    const next = points[Math.min(points.length - 1, i + 1)] as Vector3;
    const tangent = next.subtract(prev).normalize();
    let side = Vector3.Cross(tangent, reference);
    if (side.lengthSquared() < 1e-5) side = Vector3.Cross(tangent, new Vector3(1, 0, 0));
    side.normalize();
    const across = Vector3.Cross(tangent, side).normalize();
    const point = points[i] as Vector3;
    const radius = radii[i] as number;

    for (let j = 0; j < tessellation; j++) {
      const a = (j / tessellation) * Math.PI * 2;
      const offset = side.scale(Math.cos(a) * radius).add(across.scale(Math.sin(a) * radius));
      const p = point.add(offset);
      positions.push(p.x, p.y, p.z);
    }
  }

  for (let i = 0; i < points.length - 1; i++) {
    const a = i * tessellation;
    const b = (i + 1) * tessellation;
    for (let j = 0; j < tessellation; j++) {
      const j1 = (j + 1) % tessellation;
      indices.push(a + j, b + j, b + j1, a + j, b + j1, a + j1);
    }
  }

  if (capped) {
    const startCentre = positions.length / 3;
    const start = points[0] as Vector3;
    positions.push(start.x, start.y, start.z);
    const endCentre = positions.length / 3;
    const end = points[points.length - 1] as Vector3;
    positions.push(end.x, end.y, end.z);
    const endBase = (points.length - 1) * tessellation;
    for (let j = 0; j < tessellation; j++) {
      const j1 = (j + 1) % tessellation;
      indices.push(startCentre, j1, j);
      indices.push(endCentre, endBase + j, endBase + j1);
    }
  }

  const normals: number[] = [];
  VertexData.ComputeNormals(positions, indices, normals);
  const data = new VertexData();
  data.positions = positions;
  data.indices = indices;
  data.normals = normals;
  return data;
}

export function xform(
  cx: number, cy: number, cz: number,
  sx: number, sy: number, sz: number,
  yaw = 0, pitch = 0, roll = 0,
): Matrix {
  let m = Matrix.Scaling(sx, sy, sz);
  if (roll !== 0) m = m.multiply(Matrix.RotationZ(roll));
  if (pitch !== 0) m = m.multiply(Matrix.RotationX(pitch));
  if (yaw !== 0) m = m.multiply(Matrix.RotationY(yaw));
  return m.multiply(Matrix.Translation(cx, cy, cz));
}

/** A member running between two points — railings, masts, kingposts, derricks. */
export function member(a: Vector3, b: Vector3, thickness: number): Matrix {
  const dir = b.subtract(a);
  const len = dir.length();
  if (len < 1e-5) return Matrix.Identity();
  const mid = a.add(b).scale(0.5);
  const nd = dir.normalize();
  const up = new Vector3(0, 1, 0);
  const axis = Vector3.Cross(up, nd);
  const dot = Vector3.Dot(up, nd);
  let rot: Matrix;
  if (axis.lengthSquared() < 1e-9) {
    rot = dot > 0 ? Matrix.Identity() : Matrix.RotationX(Math.PI);
  } else {
    const q = Quaternion.RotationAxis(
      axis.normalize(), Math.acos(Math.max(-1, Math.min(1, dot))),
    );
    rot = new Matrix();
    q.toRotationMatrix(rot);
  }
  return Matrix.Scaling(thickness, len, thickness)
    .multiply(rot)
    .multiply(Matrix.Translation(mid.x, mid.y, mid.z));
}

/**
 * Build a ship hull as a lofted series of transverse slabs.
 *
 * A hull is not a box: it has sheer (the deck line rises toward the bow), flare (the beam
 * narrows below the waterline) and a pointed entry. Approximating that with ~14 stacked
 * slabs of varying width costs almost nothing and is the single biggest difference
 * between reading as "ship" and reading as "crate".
 *
 * Local space: +Z is forward, Y=0 is the waterline, so the caller positions a vessel by
 * its waterline exactly as the simulation reports it.
 */
export function addHull(
  acc: HullAccumulator,
  opts: {
    length: number;
    beam: number;
    draft: number;
    /** Height of the deck above the waterline amidships. */
    freeboard: number;
    /** How much the deck line rises at the bow, metres. */
    sheer: number;
    hullColor: Rgb;
    bootTopColor: Rgb;
    /** Surface treatment for the topsides; defaults to the shared worn paint scan. */
    hullSurface?: VesselSurface;
    /** Fraction of the length occupied by the pointed entry. */
    bowFraction?: number;
    segments?: number;
    /** Deck crown from edge to centre, as a fraction of beam. */
    camber?: number;
    /** How far the waterline stem sits behind the deck edge at the bow, metres. */
    bowRake?: number;
    /** Stern width as a fraction of the maximum beam. */
    sternWidth?: number;
    /** Width at the waterline relative to the deck edge. */
    flare?: number;
    /** Leave the centre open for skiffs and other cockpit boats. */
    openDeck?: boolean;
  },
): void {
  const segments = opts.segments ?? 16;
  const bowFraction = opts.bowFraction ?? 0.28;
  const camber = opts.camber ?? 0.018;
  const bowRake = opts.bowRake ?? Math.min(2.4, opts.length * 0.022);
  const sternWidth = opts.sternWidth ?? 0.76;
  const flare = opts.flare ?? 0.90;
  const halfLen = opts.length * 0.5;

  const station = (i: number): {
    z: number;
    waterZ: number;
    halfBeam: number;
    deckY: number;
  } => {
    const t = i / segments;
    const z = -halfLen + opts.length * t;
    let widthFactor = 1;
    if (t > 1 - bowFraction) {
      const bt = (t - (1 - bowFraction)) / bowFraction;
      widthFactor = Math.pow(Math.max(0, 1 - bt), 0.66);
    } else if (t < 0.14) {
      widthFactor = sternWidth + (t / 0.14) * (1 - sternWidth);
    }
    const bowT = Math.max(0, (t - (1 - bowFraction)) / bowFraction);
    const sheerHere = opts.sheer * Math.pow(Math.max(0, t - 0.32) / 0.68, 2);
    return {
      z,
      waterZ: z - bowRake * bowT * bowT,
      halfBeam: opts.beam * 0.5 * widthFactor,
      deckY: opts.freeboard + sheerHere,
    };
  };

  const topsidePositions: number[] = [];
  const bootPositions: number[] = [];
  for (let i = 0; i <= segments; i++) {
    const s = station(i);
    const waterHalf = s.halfBeam * flare;
    const crown = opts.beam * camber;

    if (opts.openDeck) {
      // Outer side, gunwale top and inner lip on each side; the gap between the inner
      // lips is intentionally not triangulated, leaving an actual open cockpit.
      topsidePositions.push(
        -waterHalf, 0, s.waterZ,
        -s.halfBeam, s.deckY, s.z,
        -s.halfBeam * 0.76, s.deckY - 0.16, s.z,
        s.halfBeam * 0.76, s.deckY - 0.16, s.z,
        s.halfBeam, s.deckY, s.z,
        waterHalf, 0, s.waterZ,
      );
    } else {
      // Port waterline → port deck edge → crowned centre → starboard deck edge → waterline.
      topsidePositions.push(
        -waterHalf, 0, s.waterZ,
        -s.halfBeam, s.deckY, s.z,
        0, s.deckY + crown, s.z,
        s.halfBeam, s.deckY, s.z,
        waterHalf, 0, s.waterZ,
      );
    }

    // The lower body turns under through a chine into a narrow keel.
    bootPositions.push(
      -waterHalf, 0, s.waterZ,
      -s.halfBeam * 0.66, -opts.draft * 0.66, s.waterZ - bowRake * 0.08,
      0, -opts.draft, s.waterZ - bowRake * 0.12,
      s.halfBeam * 0.66, -opts.draft * 0.66, s.waterZ - bowRake * 0.08,
      waterHalf, 0, s.waterZ,
    );
  }

  const stripIndices = (
    pointsPerStation: number,
    bands = Array.from({ length: pointsPerStation - 1 }, (_, i) => i),
  ): number[] => {
    const indices: number[] = [];
    for (let i = 0; i < segments; i++) {
      const a = i * pointsPerStation;
      const b = (i + 1) * pointsPerStation;
      for (const p of bands) {
        indices.push(a + p, b + p, b + p + 1, a + p, b + p + 1, a + p + 1);
      }
    }
    return indices;
  };

  const buildSurface = (
    positions: number[],
    pointsPerStation: number,
    bands?: number[],
  ): VertexData => {
    const data = new VertexData();
    const indices = stripIndices(pointsPerStation, bands);
    const normals: number[] = [];
    VertexData.ComputeNormals(positions, indices, normals);
    data.positions = positions;
    data.indices = indices;
    data.normals = normals;
    return data;
  };

  acc.add(
    buildSurface(topsidePositions, opts.openDeck ? 6 : 5, opts.openDeck ? [0, 1, 3, 4] : undefined),
    Matrix.Identity(),
    opts.hullColor,
    opts.hullSurface ?? 'paint',
  );
  acc.add(buildSurface(bootPositions, 5), Matrix.Identity(), opts.bootTopColor, 'rubber');
}

/** Deck edge bulwark — the low wall around a deck. Reads strongly in silhouette. */
export function addBulwark(
  acc: HullAccumulator,
  opts: { length: number; beam: number; deckY: number; height: number; color: Rgb },
): void {
  const halfLen = opts.length * 0.5;
  const halfBeam = opts.beam * 0.5;
  const segs = 10;
  for (let i = 0; i < segs; i++) {
    const t = (i + 0.5) / segs;
    const z = -halfLen + opts.length * t;
    const shrink = t > 0.78 ? Math.pow(1 - (t - 0.78) / 0.22, 0.6) : 1;
    const w = halfBeam * shrink;
    if (w < 0.2) continue;
    const seg = opts.length / segs;
    acc.add(box(), xform(w, opts.deckY + opts.height * 0.5, z, 0.18, opts.height, seg), opts.color, 'paint');
    acc.add(box(), xform(-w, opts.deckY + opts.height * 0.5, z, 0.18, opts.height, seg), opts.color, 'paint');
  }
}

/** A stack of decreasing blocks — the universal shape of a ship's bridge house. */
export function addSuperstructure(
  acc: HullAccumulator,
  opts: {
    z: number;
    baseY: number;
    width: number;
    depth: number;
    levels: number;
    levelHeight: number;
    color: Rgb;
    windowColor: Rgb;
  },
): void {
  let w = opts.width;
  let d = opts.depth;
  let y = opts.baseY;

  for (let i = 0; i < opts.levels; i++) {
    acc.add(box(), xform(0, y + opts.levelHeight * 0.5, opts.z, w, opts.levelHeight, d), opts.color, 'paint');

    // A continuous window band on the upper levels, inset slightly so it catches shadow.
    if (i >= opts.levels - 2) {
      const wy = y + opts.levelHeight * 0.66;
      acc.add(box(), xform(0, wy, opts.z + d * 0.5 + 0.02, w * 0.9, opts.levelHeight * 0.3, 0.12), opts.windowColor, 'glass');
      acc.add(box(), xform(0, wy, opts.z - d * 0.5 - 0.02, w * 0.9, opts.levelHeight * 0.3, 0.12), opts.windowColor, 'glass');
      acc.add(box(), xform(w * 0.5 + 0.02, wy, opts.z, 0.12, opts.levelHeight * 0.3, d * 0.9), opts.windowColor, 'glass');
      acc.add(box(), xform(-w * 0.5 - 0.02, wy, opts.z, 0.12, opts.levelHeight * 0.3, d * 0.9), opts.windowColor, 'glass');
    }

    y += opts.levelHeight;
    w *= 0.88;
    d *= 0.9;
  }
}

/** Railing run along a deck edge, as posts plus a top rail. */
export function addRailing(
  acc: HullAccumulator,
  x0: number, z0: number, x1: number, z1: number,
  y: number, height: number, color: Rgb,
): void {
  const dx = x1 - x0;
  const dz = z1 - z0;
  const len = Math.hypot(dx, dz);
  const posts = Math.max(2, Math.round(len / 2.2));
  for (let i = 0; i <= posts; i++) {
    const t = i / posts;
    acc.add(
      cylinder(6),
      xform(x0 + dx * t, y + height * 0.5, z0 + dz * t, 0.07, height, 0.07),
      color,
      'metal',
    );
  }
  const a = new Vector3(x0, y + height, z0);
  const b = new Vector3(x1, y + height, z1);
  acc.add(cylinder(6), member(a, b, 0.06), color, 'metal');
  const a2 = new Vector3(x0, y + height * 0.55, z0);
  const b2 = new Vector3(x1, y + height * 0.55, z1);
  acc.add(cylinder(6), member(a2, b2, 0.05), color, 'metal');
}
